/**
 * FundElite Design System - Auto Starter / Self Repair
 * 
 * Complete design token system for the guided onboarding experience
 */

export const FUNDELITE_DESIGN_SYSTEM = {
  // Core Brand
  brand: {
    green500: '#0FAC6C',
    green600: '#0D9F64',
    green700: '#0B8C58',
    green800: '#0B754A',
  },

  // Sidebar / Navigation
  sidebar: {
    main: '#0FAC6C',
    deep: '#0D9F64',
    darkAccent: '#0B754A',
    activeNavBg: '#56B988',
    activeNavGlow: '#8EDAB6',
  },

  // Mint / Soft Support
  mint: {
    50: '#F2FCF7',
    100: '#E5F8EE',
    200: '#D1FAE5',
    300: '#B9EFD2',
    400: '#8EDAB6',
  },

  // Backgrounds
  backgrounds: {
    app: '#F7F9F8',
    pageSurface: '#FFFFFF',
    softSection: '#F3F6F4',
    mutedSurface: '#EEF2F0',
  },

  // Borders
  borders: {
    light: '#E5E9E7',
    default: '#D9DEDC',
    strong: '#C7CFCC',
  },

  // Text
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#6B7280',
    soft: '#94A3B8',
    onGreen: '#FFFFFF',
  },

  // Functional
  functional: {
    success: '#10B981',
    successSoft: '#D1FAE5',
    warning: '#EAB23A',
    warningSoft: '#FEF3C7',
    error: '#EF4444',
    errorSoft: '#FEE2E2',
    info: '#3B82F6',
    infoSoft: '#DBEAFE',
  },

  // Buttons
  buttons: {
    primaryBg: '#0FAC6C',
    primaryHover: '#0D9F64',
    primaryPressed: '#0B8C58',
    secondaryBg: '#FFFFFF',
    secondaryBorder: '#D9DEDC',
  },

  // Glass / Premium
  glass: {
    background: 'rgba(255,255,255,0.72)',
    border: 'rgba(255,255,255,0.45)',
    highlight: 'rgba(209,250,229,0.35)',
    shadow: 'rgba(15,172,108,0.10)',
  },
};

// Onboarding flow steps definition
export const ONBOARDING_STEPS = {
  WELCOME_TERMS: 'welcome_terms',
  BASIC_INFO: 'basic_info',
  GOALS_SELECTION: 'goals_selection',
  SOCIAL_PROOF: 'social_proof',
  PACKAGE_OFFER: 'package_offer',
  PRE_CREDENTIALS_VIDEO: 'pre_credentials_video',
  CREDIT_MONITORING_CREDENTIALS: 'credit_monitoring_credentials',
  REPORT_IMPORT_LOADING: 'report_import_loading',
  DASHBOARD_READY: 'dashboard_ready',
  DASHBOARD_FIRST_USE_EDUCATION: 'dashboard_first_use_education',
};

// Step order for progression logic
export const STEP_ORDER = [
  ONBOARDING_STEPS.WELCOME_TERMS,
  ONBOARDING_STEPS.BASIC_INFO,
  ONBOARDING_STEPS.GOALS_SELECTION,
  ONBOARDING_STEPS.SOCIAL_PROOF,
  ONBOARDING_STEPS.PACKAGE_OFFER,
  ONBOARDING_STEPS.PRE_CREDENTIALS_VIDEO,
  ONBOARDING_STEPS.CREDIT_MONITORING_CREDENTIALS,
  ONBOARDING_STEPS.REPORT_IMPORT_LOADING,
  ONBOARDING_STEPS.DASHBOARD_READY,
  ONBOARDING_STEPS.DASHBOARD_FIRST_USE_EDUCATION,
];

// Step to index mapping
export const STEP_TO_INDEX = STEP_ORDER.reduce((acc, step, index) => {
  acc[step] = index + 1;
  return acc;
}, {});

// Package definition
export const AUTO_STARTER_PACKAGE = {
  id: 'complete_credit_repair_builder',
  name: 'Complete Credit Repair + Credit Builder',
  headline: 'Challenge inaccurate items and start rebuilding with a smarter plan',
  subheadline: 'Everything you need to review your report, challenge negative reporting, and move forward with a clearer action plan.',
  included: [
    { id: 'bureau_repair', title: '3-Bureau Credit Repair', description: 'Challenge items across all three bureaus' },
    { id: 'report_analysis', title: 'Personalized Report Analysis', description: 'Detailed breakdown of your credit report' },
    { id: 'creditor_challenge', title: 'Creditor / Account Challenge Support', description: 'Tools to dispute with creditors directly' },
    { id: 'advanced_review', title: 'Advanced Account Review Support', description: 'In-depth account assessment' },
  ],
  creditBuilderTools: [
    { id: 'utilization_manager', title: 'Utilization Manager', description: 'Optimize your credit utilization' },
    { id: 'payment_planner', title: 'Payment Planner', description: 'Smart payment scheduling' },
    { id: 'score_simulator', title: 'Score Simulator', description: 'See how actions affect your score' },
    { id: 'tradeline_strategy', title: 'Tradeline Strategy', description: 'Build positive credit history' },
  ],
};

// CSS variables generator for the design system
export const generateCSSVariables = () => {
  return `
    :root {
      /* Brand */
      --fe-brand-500: ${FUNDELITE_DESIGN_SYSTEM.brand.green500};
      --fe-brand-600: ${FUNDELITE_DESIGN_SYSTEM.brand.green600};
      --fe-brand-700: ${FUNDELITE_DESIGN_SYSTEM.brand.green700};
      --fe-brand-800: ${FUNDELITE_DESIGN_SYSTEM.brand.green800};
      
      /* Sidebar */
      --fe-sidebar-main: ${FUNDELITE_DESIGN_SYSTEM.sidebar.main};
      --fe-sidebar-deep: ${FUNDELITE_DESIGN_SYSTEM.sidebar.deep};
      --fe-sidebar-dark: ${FUNDELITE_DESIGN_SYSTEM.sidebar.darkAccent};
      --fe-sidebar-active-bg: ${FUNDELITE_DESIGN_SYSTEM.sidebar.activeNavBg};
      --fe-sidebar-active-glow: ${FUNDELITE_DESIGN_SYSTEM.sidebar.activeNavGlow};
      
      /* Mint */
      --fe-mint-50: ${FUNDELITE_DESIGN_SYSTEM.mint[50]};
      --fe-mint-100: ${FUNDELITE_DESIGN_SYSTEM.mint[100]};
      --fe-mint-200: ${FUNDELITE_DESIGN_SYSTEM.mint[200]};
      --fe-mint-300: ${FUNDELITE_DESIGN_SYSTEM.mint[300]};
      --fe-mint-400: ${FUNDELITE_DESIGN_SYSTEM.mint[400]};
      
      /* Backgrounds */
      --fe-bg-app: ${FUNDELITE_DESIGN_SYSTEM.backgrounds.app};
      --fe-bg-surface: ${FUNDELITE_DESIGN_SYSTEM.backgrounds.pageSurface};
      --fe-bg-soft: ${FUNDELITE_DESIGN_SYSTEM.backgrounds.softSection};
      --fe-bg-muted: ${FUNDELITE_DESIGN_SYSTEM.backgrounds.mutedSurface};
      
      /* Borders */
      --fe-border-light: ${FUNDELITE_DESIGN_SYSTEM.borders.light};
      --fe-border-default: ${FUNDELITE_DESIGN_SYSTEM.borders.default};
      --fe-border-strong: ${FUNDELITE_DESIGN_SYSTEM.borders.strong};
      
      /* Text */
      --fe-text-primary: ${FUNDELITE_DESIGN_SYSTEM.text.primary};
      --fe-text-secondary: ${FUNDELITE_DESIGN_SYSTEM.text.secondary};
      --fe-text-muted: ${FUNDELITE_DESIGN_SYSTEM.text.muted};
      --fe-text-soft: ${FUNDELITE_DESIGN_SYSTEM.text.soft};
      --fe-text-on-green: ${FUNDELITE_DESIGN_SYSTEM.text.onGreen};
      
      /* Functional */
      --fe-success: ${FUNDELITE_DESIGN_SYSTEM.functional.success};
      --fe-success-soft: ${FUNDELITE_DESIGN_SYSTEM.functional.successSoft};
      --fe-warning: ${FUNDELITE_DESIGN_SYSTEM.functional.warning};
      --fe-warning-soft: ${FUNDELITE_DESIGN_SYSTEM.functional.warningSoft};
      --fe-error: ${FUNDELITE_DESIGN_SYSTEM.functional.error};
      --fe-error-soft: ${FUNDELITE_DESIGN_SYSTEM.functional.errorSoft};
      --fe-info: ${FUNDELITE_DESIGN_SYSTEM.functional.info};
      --fe-info-soft: ${FUNDELITE_DESIGN_SYSTEM.functional.infoSoft};
      
      /* Buttons */
      --fe-btn-primary: ${FUNDELITE_DESIGN_SYSTEM.buttons.primaryBg};
      --fe-btn-primary-hover: ${FUNDELITE_DESIGN_SYSTEM.buttons.primaryHover};
      --fe-btn-primary-pressed: ${FUNDELITE_DESIGN_SYSTEM.buttons.primaryPressed};
      
      /* Glass */
      --fe-glass-bg: ${FUNDELITE_DESIGN_SYSTEM.glass.background};
      --fe-glass-border: ${FUNDELITE_DESIGN_SYSTEM.glass.border};
      --fe-glass-highlight: ${FUNDELITE_DESIGN_SYSTEM.glass.highlight};
      --fe-glass-shadow: ${FUNDELITE_DESIGN_SYSTEM.glass.shadow};
    }
  `;
};

export default FUNDELITE_DESIGN_SYSTEM;
